/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * First-run welcome modal — appears once per device, guides the new user in 3
 * concrete steps. Fires the "Try demo chat" flow in step 2 so they hear Lyra
 * within seconds (if their ElevenLabs key is set) or at least see the chat
 * animate (if not).
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XIcon, KeyIcon, SparkleIcon, HeadphonesIcon, PlayIcon } from "@phosphor-icons/react";

const KEY = "lucru_welcome_seen_v1";

const FirstRunWelcome = ({ hasKey, onOpenSettings, onTryDemo }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setOpen(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const tryDemo = () => {
    dismiss();
    onTryDemo && onTryDemo();
  };

  const openSettings = () => {
    dismiss();
    onOpenSettings && onOpenSettings();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
          data-testid="welcome-modal"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 22 }}
            className="glass rounded-2xl p-7 max-w-md w-full border border-[#222233] relative"
          >
            <button onClick={dismiss} className="absolute top-3 right-3 text-white/40 hover:text-white" data-testid="welcome-dismiss">
              <XIcon size={16} />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <SparkleIcon size={16} color="#39FF14" weight="fill" />
              <div className="text-[11px] uppercase tracking-widest text-[#39FF14] font-semibold">Welcome in</div>
            </div>
            <h2 className="font-display font-black text-2xl leading-tight mb-2">Hey, I'm Lyra. 👋</h2>
            <p className="text-sm text-white/70 leading-relaxed mb-5">
              I'll read your live chat out loud so you can keep your eyes on the game. Here's how to meet me — takes about 30 seconds.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-[#05050A] border border-[#222233] rounded-lg p-3">
                <div className="w-6 h-6 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center text-[11px] font-black text-[#00E5FF] shrink-0">1</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <KeyIcon size={13} color="#00E5FF" /> Paste your ElevenLabs API key
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">Settings → API key. 11labs.io gives you one free.</div>
                </div>
                {!hasKey && (
                  <button onClick={openSettings} className="btn-pill btn-ghost text-[10px] px-2 py-1" data-testid="welcome-open-settings">Open</button>
                )}
              </div>
              <div className="flex items-start gap-3 bg-[#05050A] border border-[#222233] rounded-lg p-3">
                <div className="w-6 h-6 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40 flex items-center justify-center text-[11px] font-black text-[#FF007F] shrink-0">2</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <HeadphonesIcon size={13} color="#FF007F" /> Hear me right now
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">Try a demo chat — no stream needed.</div>
                </div>
                <button onClick={tryDemo} className="btn-pill btn-primary text-[10px] px-2 py-1" data-testid="welcome-try-demo">
                  <PlayIcon size={9} weight="fill" /> Try
                </button>
              </div>
              <div className="flex items-start gap-3 bg-[#05050A] border border-[#222233] rounded-lg p-3">
                <div className="w-6 h-6 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 flex items-center justify-center text-[11px] font-black text-[#39FF14] shrink-0">3</div>
                <div>
                  <div className="text-sm font-semibold">Connect your channel</div>
                  <div className="text-xs text-white/50 mt-0.5">Twitch · YouTube Live · Kick · 6 cam platforms. Pick one, type your handle, hit Connect.</div>
                </div>
              </div>
            </div>

            <button
              onClick={dismiss}
              data-testid="welcome-skip"
              className="text-xs text-white/40 hover:text-white mt-5 block mx-auto"
            >
              I'll explore on my own
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FirstRunWelcome;
